import { useEffect, useRef, useState } from "react";
import { Formik, FormikProps } from "formik";
import SelectDropdown, { ISelectDropdownItem } from "../../components/form-control/SelectDropdown";
import Button from "../../components/form-control/Button";
import { CreateTransaction, getAccounts, getCustomers, initializeCompanyPayment } from "../../api/requests";
import { Account, IAccount } from "../../components/models/account/Account";
import Input from "../../components/form-control/Input";
import { ACCOUNT_TYPES, COMPANY_EXPENSE_TYPE, CUSTOMER_RELATIONSHIP_TYPES, ONLINE_PAYMENT_MODES, PAYABLES, PAYMENT_MODES, PAYMENT_SERVICES, TRANSACTION_TYPES } from "../../resources/enums";
import TextArea from "../../components/form-control/TextArea";
import { getFormattedPhoneNumber, getIDTypesList, getListFromEnum, getPhoneNumberFromShortCodeFormat, getSelectItemsFromEnum } from "../../utilities/miscFunctions";
import { ICreateTransactionDTO, IInitializeCompanyPaymentDTO, IPaymentData } from "../../resources/interfaces";
import { formDataToCreateTransactionDTO } from "../../resources/converters";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import { Transaction } from "../../components/models/transaction/Transaction";
import { capitalize } from "lodash";
import useAccounts from "../../hooks/accountsHook";
import * as Yup from "yup";
import CustomerComponent from "../customers/CustomerComponent";
import useTransactions from "../../hooks/transactionsHook";
import InputSelectDropdown from "../../components/form-control/InputSelectDropdown";
import useSearchQuery from "../../hooks/SearchQueryHook";
import { useQuery } from "@tanstack/react-query";
import AccountSummary from "../accounts/AccountSummary";
import ToastContent from "../../components/ToastContent";
import usePayments from "../../hooks/PaymentsHook";
import useModal from "../../hooks/modalHook";

interface IProps {
    type?: TRANSACTION_TYPES;
    account?: Account
    onComplete: (transaction?: Transaction) => void;
    shouldReset?: boolean;
    initiatePayment?: boolean;
}

const AddTransaction = (props: IProps): JSX.Element => {
    const { fetchAccounts, account, fetchAccountById, accounts, setAccount } = useAccounts();
    const [selectedAccount, setSelectedAccount] = useState<Account | null>(props.account ?? null);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const { TransactionTypesList } = useTransactions({ onTransactionAdded: () => { } });
    const { debouncedQuery, setQuery } = useSearchQuery();
    const { handlePayment } = usePayments({ onPaymentProcessed: () => onPaymentProcessed() });
    const { ModalComponent, updateModalStates } = useModal();
    const [initiatePayment, setInitiatePayment] = useState<boolean>(props.initiatePayment ?? false);

    const formikRef = useRef<FormikProps<any>>(null);

    const { data, isLoading, isStale, isFetching } = useQuery<any, Error>({
        queryKey: [
            'accounts',
            debouncedQuery,
        ],
        queryFn: () => fetchAccounts({ query: debouncedQuery })
    });

    useEffect(() => {
        if (account) {
            setSelectedAccount(account);
        }
    }, [account])

    const initialValues = {
        account_id: props.account?.id ?? "",
        amount: props.type === TRANSACTION_TYPES.CREDIT ? selectedAccount?.rate ?? 0 : 0,
        type: props.type ?? "",
        transaction_by: selectedAccount?.customer?.name ?? "",
        transactor_id_number: selectedAccount?.customer?.primaryContact?.id_number ?? "",
        transactor_id_type: selectedAccount?.customer?.primaryContact?.id_type ?? "",
        transactor_phone: selectedAccount?.customer?.phone ?? "",
        description: "",
        transactor_address: selectedAccount?.customer?.address?.street_address ?? "",
        transaction_mode: ""
    }

    const submitHandler = (values: ICreateTransactionFormData) => {


        setSubmitting(true);

        if (initiatePayment) {
            const payload: IInitializeCompanyPaymentDTO = {
                amount: values.amount,
                notes: values.description,
                provider: PAYMENT_SERVICES.PAYSTACK,
                payment_type: COMPANY_EXPENSE_TYPE.OTHER,
                payable: PAYABLES.ACCOUNT,
                payable_id: values.account_id
            };

            initializeCompanyPayment(payload)
                .then((response: IPaymentData) => {
                    if (response) {
                        handlePayment(response);
                        // props.onComplete();
                    }
                }).catch((error: any | AxiosError | Error) => {
                    toast(
                        <ToastContent
                            title={error.message}
                            description={error.detail}
                            status="error"
                        />,
                        {
                            autoClose: 5000
                        }
                    );
                    console.log("error response", error);
                }).finally(() => setSubmitting(false))
        } else {
            const payload: ICreateTransactionDTO = {
                ...formDataToCreateTransactionDTO(values),
                transaction_by: values.transaction_by ? values.transaction_by : selectedAccount?.customer?.name ?? "",
                transactor_phone: getFormattedPhoneNumber(values.transactor_phone ? values.transactor_phone : selectedAccount?.customer?.phone ?? "")
            }

            CreateTransaction(payload)
                .then((response: any) => {
                    if (response) {
                        const transaction = new Transaction(response);
                        props.onComplete(transaction);
                        setAccount(transaction.account);
                        if (props.shouldReset) {
                            // formikRef.current?.resetForm();
                        }
                    }
                    toast.success(`${payload.type === TRANSACTION_TYPES.CREDIT ? "Account credited successfully" : "Account debited successfully"}`, { autoClose: 5000 });
                }).catch((error: any | AxiosError | Error) => {
                    toast.error(error.detail, { autoClose: 5000 });
                }).finally(() => setSubmitting(false))
        }
    }

    const onPaymentProcessed = () => {
        props.onComplete();
    }

    const isOnlinePayment = (paymentOption: PAYMENT_MODES): boolean => {
        return (
            paymentOption === PAYMENT_MODES.MOBILE_MONEY ||
            paymentOption === PAYMENT_MODES.CARD ||
            paymentOption === PAYMENT_MODES.BANK_TRANSFER
        );
    }

    const initiateOnlinePayment = () => {
        setInitiatePayment(true);
        formikRef.current?.submitForm();
    }

    return (
        <div>
            {selectedAccount && (
                <AccountSummary account={selectedAccount} />
            )}
            <Formik
                initialValues={initialValues}
                innerRef={formikRef}
                validationSchema={Yup.object({
                    amount: Yup.number()
                        .required('Amount is required'),
                    account_id: Yup.string()
                        .trim()
                        .required('Account is required'),
                    transaction_mode: Yup.string()
                        .trim()
                        .required('Transaction mode is required'),
                    type: Yup.string()
                        .trim()
                        .required('Transaction type is required'),
                    // susu_cycle_id: Yup.string().when("type", {
                    //     is: TRANSACTION_TYPES.DEBIT,
                    //     then: (schema) =>
                    //         selectedAccount?.isSUSU()
                    //             ? schema.required("Susu cycle is required")
                    //             : schema,
                    //     otherwise: (schema) => schema.notRequired(),
                    // }),
                })}
                onSubmit={(values: ICreateTransactionFormData) => {
                    setInitiatePayment(initiatePayment ?? props.initiatePayment);
                    submitHandler(values);
                }}>
                {({ isSubmitting, values, handleBlur, handleChange, handleSubmit, setFieldValue, touched, errors }) => (
                    <form onSubmit={handleSubmit} className="mt-4">
                        <div className="flex flex-col gap-4">
                            <InputSelectDropdown
                                list={accounts?.map((account: Account) => (
                                    {
                                        name: <CustomerComponent
                                            customer={account.customer && account.customer}
                                            avatarSize="xs"
                                            metaData={<div className="text-xs dark:text-text-dark-secondary">
                                                {account.type} | {account.accountNumber} | Balance: {account.computedBalance}
                                            </div>}
                                        />,
                                        value: account.id,
                                        id: account.id,
                                        label: account.name
                                    }
                                ))}
                                name="account_id"
                                // listSelectedValue={props.account && props.account.id ? props.account.id : values.account_id}
                                listSelectedValue={selectedAccount && selectedAccount.id ? selectedAccount.id : values.account_id}
                                onChange={(value: string) => {
                                    if (value) {
                                        const _account: Account | undefined = accounts.find((acc: Account) => acc.id === value);
                                        setSelectedAccount(_account ?? null);
                                        setFieldValue("susu_cycle_id", _account?.cyclesAvailableForWithdrawal?.[0]?.id);

                                        if (!_account) {
                                            fetchAccountById(value);
                                        }

                                    } else {
                                        setSelectedAccount(null);
                                    }
                                    setFieldValue("account_id", value);
                                }}
                                label="Account"
                                // wrapperClasses="w-full rounded"
                                placeholder="Search by name or account number"
                                disabled={!!props.account && !props.shouldReset}
                                onSearchChange={setQuery}
                                error={touched.account_id ? errors.account_id : undefined
                                }
                                helperText={touched?.account_id ? errors.account_id : undefined
                                }
                            />

                            {!props.initiatePayment && (
                                <SelectDropdown
                                    list={TransactionTypesList}
                                    listSelectedValue={values.type}
                                    onChange={(value: string) => {
                                        setFieldValue("type", value);
                                    }}
                                    label="Transaction Type"
                                    wrapperClasses="w-full rounded"
                                    placeholder="Select type"
                                    error={touched.type ? errors.type : undefined
                                    }
                                    helperText={touched?.type ? errors.type : undefined
                                    }
                                />
                            )}

                            {/* {
                                selectedAccount?.isSUSU()
                                && values.type === TRANSACTION_TYPES.DEBIT
                                && selectedAccount?.cyclesAvailableForWithdrawal
                                && selectedAccount?.cyclesAvailableForWithdrawal?.length > 0 && (
                                    <SelectDropdown
                                        list={selectedAccount?.cyclesAvailableForWithdrawal.map((cycle: SusuCycle) => (
                                            {
                                                name: <div>
                                                    <div>Cycle Number {cycle.cycle_number}</div>
                                                    <div>
                                                        Withdrawable: {cycle.available_balance} | Total Deposited: {cycle.total_deposited}
                                                    </div>
                                                </div>,
                                                value: cycle.id,
                                                id: cycle.id,
                                                label: `Cycle Number ${cycle.cycle_number}`
                                            }
                                        ))}
                                        listSelectedValue={values.susu_cycle_id}
                                        description={"Susu withdrawal can only be requested per the oldest completed or active cycle."}
                                        onChange={(value: string) => {
                                            setFieldValue("susu_cycle_id", value);
                                        }}
                                        label="Withdrawable Cycle"
                                        wrapperClasses="w-full rounded"
                                        placeholder="Select cycle"
                                        error={touched.susu_cycle_id ? errors.susu_cycle_id : undefined
                                        }
                                        helperText={touched?.susu_cycle_id ? errors.susu_cycle_id : undefined
                                        }
                                        disabled
                                    />
                                )} */}

                            <Input
                                type="number"
                                value={values.amount}
                                name="amount"
                                label="Amount"
                                placeholder="Enter amount"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                required
                                error={touched.amount ? errors.amount : undefined
                                }
                                helperText={touched?.amount ? errors.amount : undefined
                                }
                                pattern="\d*"
                                min={1}
                                description={`${values.type === TRANSACTION_TYPES.DEBIT ? "Max withdrawable balance: " + selectedAccount?.availableBalnce : props.account?.type === ACCOUNT_TYPES.SUSU ? `${capitalize(props.account.payment_cycle)} Rate: GHS ${props.account.rate}` : ""}`}
                                max={values.type === TRANSACTION_TYPES.DEBIT && selectedAccount?.type === ACCOUNT_TYPES.SUSU ? selectedAccount.availableBalnce : undefined}
                            />

                            <SelectDropdown
                                list={props.initiatePayment ? getSelectItemsFromEnum(ONLINE_PAYMENT_MODES) : getSelectItemsFromEnum(PAYMENT_MODES)}
                                listSelectedValue={values.transaction_mode}
                                onChange={(value: string) => {
                                    setFieldValue("transaction_mode", value);
                                }}
                                label="Transaction Mode"
                                wrapperClasses="w-full rounded"
                                placeholder="Select mode"
                                required
                                error={touched.transaction_mode ? errors.transaction_mode : undefined
                                }
                                helperText={touched?.transaction_mode ? errors.transaction_mode : undefined
                                }
                            />

                            {/* <Input
                                type="text"
                                value={values.transaction_by}
                                name="transaction_by"
                                label="Transaction By"
                                placeholder="Enter the full name of the transactor"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                required
                                error={touched.transaction_by ? errors.transaction_by : undefined
                                }
                                helperText={touched?.transaction_by ? errors.transaction_by : undefined
                                }
                            /> */}

                            {/* <IntlPhoneInput
                                value={values.transactor_phone}
                                label="Transactor's phone number"
                                placeholder="Enter phone number transactor"
                                onBlur={handleBlur}
                                onChange={(phone) => {
                                    setFieldValue("transactor_phone", phone);
                                }}
                                error={touched.transactor_phone ? errors.transactor_phone : undefined
                                }
                            // helperText={touched?.transactor_phone ? errors.transactor_phone : undefined
                            // }
                            /> */}

                            {/* <SelectDropdown
                                list={getIDTypesList()}
                                listSelectedValue={values.transactor_id_type}
                                label="Transactor's ID Type"
                                placeholder="Select transactor ID type"
                                wrapperClasses="rounded"
                                onBlur={handleBlur}
                                onChange={(value: string) => {
                                    setFieldValue("transactor_id_type", value);
                                }}
                                required
                                error={touched.transactor_id_type ? errors.transactor_id_type : undefined
                                }
                                helperText={touched?.transactor_id_type ? errors.transactor_id_type : undefined
                                }
                            /> */}

                            {/* <Input
                                type="text"
                                value={values.transactor_id_number}
                                name="transactor_id_number"
                                label="Transactor's ID Number"
                                placeholder="GHA-000000000-0"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                required
                                error={touched.transactor_id_number ? errors.transactor_id_number : undefined
                                }
                                helperText={touched?.transactor_id_number ? errors.transactor_id_number : undefined
                                }
                            /> */}

                            {/* <Input
                                type="text"
                                value={values.transactor_address}
                                name="transactor_address"
                                label="Transactor's Residential Address"
                                placeholder="Enter residential address of transactor"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                required
                                error={touched.transactor_address ? errors.transactor_address : undefined
                                }
                                helperText={touched?.transactor_address ? errors.transactor_address : undefined
                                }
                            /> */}

                            <TextArea
                                value={values.description}
                                name="description"
                                label="Description"
                                placeholder="Enter description"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={touched.description ? errors.description : undefined
                                }
                                helperText={touched?.description ? errors.description : undefined
                                }
                                rows={2}
                            />
                            {/* {
                                selectedAccount?.isSUSU()
                                && values.type === TRANSACTION_TYPES.DEBIT
                                && selectedAccount?.cyclesAvailableForWithdrawal?.length === 0 && (
                                    <span className="text-danger-main">No cycle available with a withdrawable balance</span>
                                )} */}
                        </div>

                        <div className="flex justify-end mt-8 gap-x-2">
                            {!props.initiatePayment &&
                                isOnlinePayment(values.transaction_mode as PAYMENT_MODES) &&
                                values.type === TRANSACTION_TYPES.CREDIT &&
                                (
                                    <Button
                                        label="Initiate Payment"
                                        onClick={initiateOnlinePayment}
                                        disabled={submitting || values.amount <= 0}
                                        loading={submitting}
                                        className="px-12 bg-primary-main rounded hover:bg-white text-white hover:text-primary-main hover:border-primary-main"
                                    />
                                )}

                            <Button
                                label="Submit"
                                type="submit"
                                onClick={handleSubmit}
                                disabled={submitting || (selectedAccount?.isSUSU()
                                    && values.type === TRANSACTION_TYPES.DEBIT
                                    && selectedAccount?.cyclesAvailableForWithdrawal?.length === 0)}
                                loading={submitting && !initiatePayment}
                                className="px-12 bg-secondary-main rounded hover:bg-white hover:border-secondary-main"
                            />
                        </div>
                    </form>
                )}
            </Formik>
            <ModalComponent />
        </div>
    )
}

export default AddTransaction;

export interface ICreateTransactionFormData {
    account_id: string;
    amount: number;
    type: string;
    transaction_by: string;
    transactor_id_number: string;
    transactor_id_type: string;
    transactor_phone: string;
    description: string;
    transactor_address: string;
    transaction_mode: string;
}